import React, { FC, memo, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { IconProps } from '../../assets/icons/icon.types';
import { ChevronLeftIcon } from '../../assets/icons/index';
import { StringKey } from '../../lang/index';
import { AppFormattedMessage } from '../app-formatted-message/index';
import { DropDown } from '../dropdown/index';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../hover-card/index';
import { closeAllDropdowns, createDropdownStore } from './side-bar.store';

export type SideBarItemProps = {
  className?: string;
  dropdownEnabled?: boolean;
  Icon: FC<IconProps>;
  iconBadgeDot?: boolean;
  isExpanded: boolean;
  isSelected: boolean;
  onClick?: () => void;
  specialIconColor?: string;
  specialTitleColor?: string;
  subContent?: ReactNode;
  title: StringKey;
};

export const SideBarItem: FC<SideBarItemProps> = memo(
  ({
    dropdownEnabled = true,
    Icon,
    iconBadgeDot = false,
    isExpanded,
    isSelected,
    onClick,
    title,
    className,
    specialIconColor,
    specialTitleColor,
    subContent,
  }) => {
    const disabled = !subContent || !dropdownEnabled;
    const showSubmenuIndicator = Boolean(subContent) && !dropdownEnabled && isExpanded;
    const showAttentionDot = Boolean(iconBadgeDot);

    const dropdownStore = disabled ? undefined : createDropdownStore(title);

    const isOpen = dropdownStore?.(({ isOpen }) => isOpen);
    const toggler = dropdownStore?.(({ toggler }) => toggler);

    const Wrapper: FC<{ children: ReactNode }> = ({ children }) =>
      subContent && (!dropdownEnabled || !isExpanded) ? (
        <HoverCard openDelay={150}>
          <HoverCardTrigger>{children}</HoverCardTrigger>

          <HoverCardContent
            align="start"
            className="mb-4 rounded-md border border-gray-200 bg-gray-25 p-2 shadow-3xl"
            isShownTriangle={false}
            side="right"
            sideOffset={8}
          >
            {subContent}
          </HoverCardContent>
        </HoverCard>
      ) : (
        <>{children}</>
      );

    return (
      <DropDown
        answer={subContent}
        className={{
          questionLabelWrapper: 'rounded-lg p-0 hover:bg-white',
          questionIcon: twMerge(!isExpanded && 'hidden'),
          questionLabel: 'w-full text-sm text-[#2565C8]',
          iconColor: isSelected
            ? {
                closed: '#2565C8',
                open: '#2565C8',
              }
            : {
                closed: '#98A2B3',
                open: '#98A2B3',
              },
          wrapper: twMerge(
            'w-full rounded-lg p-0 hover:bg-white',
            isSelected && 'rounded-lg bg-white shadow-sm',
            isExpanded && 'pr-1',
          ),
          answerWrapper: 'mt-0',
        }}
        defaultValue={isOpen}
        disabled={disabled}
        onStateChange={toggler}
        question={
          <Wrapper>
            <div
              className={twMerge(
                'group relative flex w-10 cursor-pointer items-center gap-[10px] rounded-lg transition-all duration-300',
                isExpanded && 'w-full pr-2',
                className,
              )}
              onClick={() => {
                if (subContent) return;
                closeAllDropdowns();
                onClick?.();
              }}
            >
              <div
                className={twMerge(
                  'flex size-10 shrink-0 items-center justify-center rounded-lg',
                  !specialIconColor &&
                    !isSelected &&
                    'text-[#98A2B3] hover:shadow-xs group-hover:text-[#475467]',
                )}
              >
                {Icon ? (
                  <Icon
                    height={20}
                    iconColor={
                      specialIconColor ? specialIconColor : isSelected ? '#2565C8' : 'currentColor'
                    }
                    width={20}
                  />
                ) : null}
              </div>
              <span
                className={twMerge(
                  'w-fit overflow-hidden text-nowrap text-sm font-[450]',
                  specialTitleColor
                    ? specialTitleColor
                    : isSelected
                      ? 'text-brand-700'
                      : 'text-[#98A2B3] group-hover:text-[#475467]',
                )}
              >
                <AppFormattedMessage id={title} />
              </span>
              {showSubmenuIndicator && (
                <ChevronLeftIcon className="ml-auto size-4 rotate-180 text-gray-400" />
              )}
              {showAttentionDot ? (
                <span
                  aria-hidden
                  className="pointer-events-none absolute right-[3px] top-[3px] size-[6px] rounded-full bg-fireside-500"
                />
              ) : null}
            </div>
          </Wrapper>
        }
      />
    );
  },
);

SideBarItem.displayName = 'SideBarItem';
